import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Table, TableHead, TableBody, TableRow, TableCell, Chip, Button, Alert,
  Stack, LinearProgress, FormControlLabel, Switch, TextField,
} from '@mui/material';
import { Refresh, ContentCopy, CheckCircle, Warning, Error as ErrorIcon, Info } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import { useAuthStore } from '../../store/auth';

// Security monitoring: attacks, intrusion prevention, logins, fraud and exposure (checks are read-only).
// The only action is the super-admin-only permanent IP block list (all ports, applied by ce-ipblock-sync).

type Status = 'ok' | 'warn' | 'crit' | 'info';
const META: Record<Status, { label: string; color: 'success' | 'warning' | 'error' | 'default'; icon: React.ReactElement }> = {
  ok: { label: 'OK', color: 'success', icon: <CheckCircle fontSize="small" /> },
  warn: { label: 'Warning', color: 'warning', icon: <Warning fontSize="small" /> },
  crit: { label: 'Critical', color: 'error', icon: <ErrorIcon fontSize="small" /> },
  info: { label: 'Info', color: 'default', icon: <Info fontSize="small" /> },
};
const AREAS = ['Patch management', 'Intrusion prevention', 'SIP / VoIP', 'SSH access', 'Web portal & API', 'Accounts & 2FA', 'Toll fraud', 'Network exposure'];
const t = (s?: string) => (s ? new Date(s).toLocaleString('en-GB', { timeZone: 'UTC' }) + ' UTC' : '—');

const MiniTable: React.FC<{ title: string; cols: string[]; rows: any[][]; empty?: string }> = ({ title, cols, rows, empty }) => (
  <Card variant="outlined">
    <CardContent sx={{ pb: '8px !important' }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>{title}</Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead><TableRow>{cols.map((c) => <TableCell key={c} sx={{ fontWeight: 700 }}>{c}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {rows.map((r, i) => <TableRow key={i}>{r.map((v, j) => <TableCell key={j} sx={{ fontVariantNumeric: 'tabular-nums' }}>{v}</TableCell>)}</TableRow>)}
            {!rows.length ? <TableRow><TableCell colSpan={cols.length} sx={{ color: 'text.secondary' }}>{empty || 'None'}</TableCell></TableRow> : null}
          </TableBody>
        </Table>
      </Box>
    </CardContent>
  </Card>
);

export const SecurityMonitorPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [auto, setAuto] = useState(false);
  const role = useAuthStore(s => s.user?.role);
  const isSuper = role === 'super_admin';
  const [blocks, setBlocks] = useState<any[]>([]);
  const [newIp, setNewIp] = useState('');
  const [newReason, setNewReason] = useState('');
  const loadBlocks = useCallback(async () => {
    try { const { data: b } = await api.get('/security-monitor/ip-blocks'); setBlocks(Array.isArray(b) ? b : []); } catch { /* not permitted */ }
  }, []);
  useEffect(() => { loadBlocks(); }, [loadBlocks]);
  const activeBlocked = useMemo(() => new Set(blocks.filter(b => b.active).map(b => b.ip)), [blocks]);
  const blockIp = async (ip: string, reason: string) => {
    if (!window.confirm(`Block ${ip} permanently on all ports?`)) return;
    try {
      const { data: r } = await api.post('/security-monitor/ip-blocks', { ip, reason });
      enqueueSnackbar(r.already_blocked ? `${ip} was already blocked` : `${ip} blocked permanently${r.applied ? '' : ' (firewall applies within a minute)'}`, { variant: 'success' });
      setNewIp(''); setNewReason(''); loadBlocks();
    } catch (e: any) { enqueueSnackbar(e?.response?.data?.message || 'Block failed', { variant: 'error' }); }
  };
  const unblockIp = async (b: any) => {
    if (!window.confirm(`Remove the permanent block on ${b.ip}?`)) return;
    try { await api.delete(`/security-monitor/ip-blocks/${b.id}`); enqueueSnackbar(`${b.ip} unblocked`, { variant: 'success' }); loadBlocks(); }
    catch (e: any) { enqueueSnackbar(e?.response?.data?.message || 'Unblock failed', { variant: 'error' }); }
  };

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { const { data: d } = await api.get('/security-monitor', { timeout: 90000 }); setData(d); }
    catch (e: any) { setError(e?.response?.data?.message || 'Security check could not run. Call the super admin.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!auto) return undefined; const i = setInterval(load, 120000); return () => clearInterval(i); }, [auto, load]);

  const groups = useMemo(() => {
    const g: Record<string, any[]> = {};
    for (const f of data?.findings || []) (g[f.area] = g[f.area] || []).push(f);
    return AREAS.filter((a) => g[a]).map((a) => ({ area: a, items: g[a] }));
  }, [data]);

  const copy = async () => {
    if (!data) return;
    const probs = (data.findings || []).filter((f: any) => f.status === 'crit' || f.status === 'warn');
    const txt = [`esbc2 security check — ${t(data.checked_at)} — ${META[data.level as Status].label.toUpperCase()}`,
      `${data.summary.crit} critical, ${data.summary.warn} warning`, '',
      ...(probs.length ? probs.flatMap((f: any) => [
        `[${META[f.status as Status].label}] ${f.area} — ${f.name}: ${f.value}${f.detail ? ` (${f.detail})` : ''}`,
        ...(f.affected?.length ? ['   Affected:', ...f.affected.map((a: string) => `   - ${a}`)] : []),
        ...(f.advice ? [`   Action: ${f.advice}`] : []), '',
      ]) : ['No security problems found.'])].join('\n');
    try { await navigator.clipboard.writeText(txt); enqueueSnackbar('Report copied — paste it to the super admin', { variant: 'success' }); }
    catch { enqueueSnackbar('Copy failed', { variant: 'error' }); }
  };

  const d = data?.details || {};
  const level: Status = data?.level || 'info';

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" fontWeight={700}>Security Monitor</Typography>
          <Typography variant="body2" color="text.secondary">Read-only view of attacks, blocked intruders, logins, fraud alerts and network exposure. Nothing here changes the server.</Typography>
        </Box>
        <FormControlLabel control={<Switch checked={auto} onChange={(e) => setAuto(e.target.checked)} />} label="Auto-refresh (2 min)" />
        <Button variant="outlined" startIcon={<ContentCopy />} disabled={!data} onClick={copy}>Copy report</Button>
        <Button variant="contained" startIcon={<Refresh />} disabled={loading} onClick={load}>Refresh</Button>
      </Stack>
      {loading ? <LinearProgress sx={{ mb: 2 }} /> : null}
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      {data ? (
        <>
          <Alert severity={level === 'crit' ? 'error' : level === 'warn' ? 'warning' : 'success'} sx={{ mb: 2 }}>
            <Typography fontWeight={700}>{level === 'crit' ? 'CRITICAL security finding — call the Super Admin now' : level === 'warn' ? 'Security warnings to review' : 'No security problems found'}</Typography>
            <Typography variant="body2">{data.summary.crit} critical · {data.summary.warn} warning · checked {t(data.checked_at)}{data.cached ? ' (cached, under 1 min old)' : ''}</Typography>
            {level !== 'ok' ? <Typography variant="body2" sx={{ mt: 1 }}>Use <b>Copy report</b> and send it to the Super Admin. Do not attempt changes yourself.</Typography> : null}
          </Alert>

          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, mb: 3 }}>
            {groups.map((g) => {
              const worst: Status = g.items.some((f: any) => f.status === 'crit') ? 'crit' : g.items.some((f: any) => f.status === 'warn') ? 'warn' : 'ok';
              return (
                <Card key={g.area} variant="outlined">
                  <CardContent sx={{ pb: '8px !important' }}>
                    <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1 }}>{g.area}</Typography>
                      <Chip size="small" icon={META[worst].icon} label={META[worst].label} color={META[worst].color} />
                    </Stack>
                    <Table size="small"><TableBody>
                      {g.items.map((f: any) => (
                        <TableRow key={f.name}>
                          <TableCell sx={{ width: 110, pl: 0 }}><Chip size="small" icon={META[f.status as Status].icon} label={META[f.status as Status].label} color={META[f.status as Status].color} variant={f.status === 'info' ? 'outlined' : 'filled'} /></TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{f.name}</Typography>
                            {f.detail ? <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{f.detail}</Typography> : null}
                            {(f.status === 'warn' || f.status === 'crit') && (f.affected?.length || f.advice) ? (
                              <Box sx={{ mt: 1, p: 1, borderRadius: 1, bgcolor: f.status === 'crit' ? 'error.main' : 'warning.main', color: '#fff', opacity: 0.92 }}>
                                {f.affected?.length ? (
                                  <>
                                    <Typography variant="caption" fontWeight={700} sx={{ display: 'block' }}>Affected ({f.affected.length}):</Typography>
                                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                      {f.affected.map((a: string) => <li key={a}><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{a}</Typography></li>)}
                                    </Box>
                                  </>
                                ) : null}
                                {f.advice ? <Typography variant="caption" sx={{ display: 'block', mt: f.affected?.length ? 0.75 : 0 }}><b>What the super admin should do:</b> {f.advice}</Typography> : null}
                              </Box>
                            ) : null}
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{f.value}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody></Table>
                  </CardContent>
                </Card>
              );
            })}
          </Box>

          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>Details</Typography>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' } }}>
            <MiniTable title="Pending updates (not yet installed)" cols={['Package', 'Installed', 'Available', 'Type']}
              rows={(d.patches?.pending || []).map((x: any) => [x.pkg, x.from, x.to, x.security ? 'SECURITY' : x.critical ? 'live service — window only' : 'normal'])} empty="Everything is up to date" />
            <MiniTable title="Installed products and versions" cols={['Product', 'Version', 'Update / note', 'Source']}
              rows={(d.patches?.inventory || []).map((x: any) => [x.product, x.version, x.update || '—', x.source])} />
            <MiniTable title="Safe patching procedure (super admin, maintenance window)" cols={['Step']}
              rows={(d.patches?.procedure || []).map((x: string, i: number) => [`${i + 1}. ${x}`])} />
            <MiniTable title="Automatic security updates" cols={['Setting', 'Value']}
              rows={d.patches?.auto_update ? [
                ['Enabled', d.patches.auto_update.enabled ? 'yes (daily ~06:00 UTC)' : 'no'],
                ['Last automatic install', d.patches.auto_update.last_run ? `${d.patches.auto_update.last_run} UTC — ${d.patches.auto_update.last_packages}` : '—'],
                ['Excluded from auto-update', (d.patches.auto_update.blacklist || []).join(', ') || 'nothing excluded'],
                ['Automatic reboot', d.patches.auto_update.auto_reboot ? 'ON' : 'off'],
              ] : []} />
            <MiniTable title="Fail2ban jails" cols={['Jail', 'Banned now', 'Banned total', 'Failing now']}
              rows={(d.intrusion?.jails || []).map((j: any) => [j.jail, j.banned_now, j.banned_total, j.failed_now])} />
            <MiniTable title="Most-banned attacker IPs (24 h)" cols={['IP', 'Bans']} rows={(d.intrusion?.top_banned_ips_24h || []).map((x: any) => [x.key, x.count])} />
            <MiniTable title="Rejected SIP attempts by reason (24 h)" cols={['Reason', 'Attempts', 'Source IPs']} rows={(d.sip?.reasons_24h || []).map((x: any) => [x.reason, x.n, x.ips])} />
            <MiniTable title="Top rejected SIP sources (24 h)" cols={['IP', 'Attempts']} rows={(d.sip?.top_source_ips_24h || []).map((x: any) => [x.key, x.count])} />
            <MiniTable title="Successful SSH logins (24 h) — verify these are yours" cols={['Time', 'User', 'Method', 'From IP']}
              rows={(d.ssh?.successful_logins_24h || []).map((x: any) => [x.at, x.user, x.method, x.ip])} empty="No SSH logins" />
            <MiniTable title="Top SSH attackers (24 h)" cols={['IP', 'Failed attempts']} rows={(d.ssh?.top_failed_ips_24h || []).map((x: any) => [x.key, x.count])} />
            <MiniTable title="Usernames attackers tried (24 h)" cols={['Username', 'Attempts']} rows={(d.ssh?.top_tried_usernames_24h || []).map((x: any) => [x.key, x.count])} />
            <MiniTable title="Failed portal logins by IP (24 h)" cols={['IP', 'Failures']} rows={(d.web?.top_failed_login_ips_24h || []).map((x: any) => [x.key, x.count])} />
            <MiniTable title="Web vulnerability scanners (24 h)" cols={isSuper ? ['IP', 'Probe requests', 'Action'] : ['IP', 'Probe requests', 'Status']}
              rows={(d.web?.top_scanner_ips_24h || []).map((x: any) => [x.key, x.count,
                activeBlocked.has(x.key) ? <Chip size="small" color="error" label="Blocked permanently" />
                  : isSuper ? <Button size="small" color="error" variant="outlined" onClick={() => blockIp(x.key, `Web vulnerability scanner (${x.count} probes in 24 h)`)}>Block</Button>
                  : <Typography variant="caption" color="text.secondary">not blocked</Typography>])} />
            <Card variant="outlined">
              <CardContent sx={{ pb: '8px !important' }}>
                <Typography variant="subtitle2" fontWeight={700}>Permanent IP blocks (all ports)</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Dropped by the firewall on every port. Customer trunks, carriers, SIP devices, operator IPs and this server can never be blocked.
                </Typography>
                {isSuper ? (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
                    <TextField size="small" label="IP address" value={newIp} onChange={e => setNewIp(e.target.value)} />
                    <TextField size="small" label="Reason" value={newReason} onChange={e => setNewReason(e.target.value)} sx={{ flexGrow: 1 }} />
                    <Button variant="contained" color="error" disabled={!newIp.trim()} onClick={() => blockIp(newIp.trim(), newReason.trim())}>Block</Button>
                  </Stack>
                ) : null}
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead><TableRow>{['IP', 'Reason', 'Blocked by', 'Since', 'Status', ''].map(c => <TableCell key={c} sx={{ fontWeight: 700 }}>{c}</TableCell>)}</TableRow></TableHead>
                    <TableBody>
                      {blocks.map((b: any) => (
                        <TableRow key={b.id}>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{b.ip}</TableCell>
                          <TableCell>{b.reason || '—'}</TableCell>
                          <TableCell>{b.created_by || '—'}</TableCell>
                          <TableCell>{t(b.created_at)}</TableCell>
                          <TableCell>{b.active ? <Chip size="small" color="error" label="Blocked" /> : <Chip size="small" label={`Removed ${t(b.removed_at)}`} />}</TableCell>
                          <TableCell>{isSuper && b.active ? <Button size="small" onClick={() => unblockIp(b)}>Unblock</Button> : null}</TableCell>
                        </TableRow>
                      ))}
                      {!blocks.length ? <TableRow><TableCell colSpan={6} sx={{ color: 'text.secondary' }}>No permanent blocks</TableCell></TableRow> : null}
                    </TableBody>
                  </Table>
                </Box>
              </CardContent>
            </Card>
            <MiniTable title={`Web requests (24 h): ${Number(d.web?.requests_24h || 0).toLocaleString()}`} cols={['Status class', 'Requests']} rows={(d.web?.http_status_24h || []).map((x: any) => [x.key, x.count])} />
            <MiniTable title="Super-admin logins (24 h)" cols={['Time', 'User', 'Role', 'From IP']} rows={(d.accounts?.admin_logins_24h || []).map((x: any) => [t(x.at), x.username, x.role, x.ip])} />
            <MiniTable title="Staff accounts without 2FA" cols={['User', 'Role']} rows={(d.accounts?.staff_without_2fa || []).map((x: any) => [x.username, x.role])} empty="All staff use 2FA" />
            <MiniTable title="Recent fraud / traffic alerts" cols={['Time', 'Type', 'Severity', 'Customer', 'Ack']}
              rows={(d.fraud?.recent_alerts || []).map((x: any) => [t(x.created_at), x.alert_type, x.severity, x.customer || '—', x.acknowledged ? 'yes' : 'no'])} />
            <MiniTable title="Firewall allow rules" cols={['Rule']} rows={(d.exposure?.firewall_rules || []).map((r: string) => [r])} />
            <MiniTable title="Services listening on public interfaces" cols={['Listen', 'Proto', 'Reachability']} rows={(d.exposure?.listening_services || []).map((x: any) => [x.listen, x.proto, x.firewall])} />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>Checks took {(data.duration_ms / 1000).toFixed(1)} s · every view is recorded in the audit log.</Typography>
        </>
      ) : null}
    </Box>
  );
};

export default SecurityMonitorPage;
